import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

/**
 * model-viewer 3.x: its neutral tone mapping keeps filament colours true, unlike the ACES-only
 * 1.12 build that Shopify.loadFeatures provides.
 */
const MODEL_VIEWER_URL = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';

/** @type {Promise<unknown> | undefined} */
let modelViewerReady;

function loadModelViewer() {
  modelViewerReady ??= customElements.get('model-viewer')
    ? Promise.resolve()
    : import(MODEL_VIEWER_URL).then(() => customElements.whenDefined('model-viewer'));

  return modelViewerReady;
}

/**
 * Colour accuracy recipe, measured against Patron's print photos:
 * - The material is a fully rough metal, so lighting is tinted by the filament colour only. A
 *   plastic (dielectric) material adds a white specular sheen that washes out saturated colours.
 * - Tone mapping is turned off (see #disableToneMapping); every built-in curve desaturates bright
 *   colours such as Baby Blue.
 * - FLAT_SHARE of the colour is self-lit (emissive) so shaded sides stay close to the filament
 *   colour; LIGHT_GAIN lifts the result so lit faces land on the palette hex.
 */
const FLAT_SHARE = 0.65;
const LIGHT_GAIN = 1.16;

/** three.js NoToneMapping. */
const NO_TONE_MAPPING = 0;

/**
 * glTF colour factors are linear; palette hexes are sRGB.
 * @param {string} hex - e.g. "#3EA96B"
 * @returns {number[]} linear RGBA
 */
function hexToLinearRgba(hex) {
  const linear = [1, 3, 5].map((i) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });

  return [...linear, 1];
}

/**
 * Tileable marble texture: base fill plus deterministic flecks. One tile spans 40 mm of the model,
 * so fleck count and size are chosen to match the keychain preview's grain at screen size.
 * @param {{ hex: string, fleck: string }} color
 * @returns {string} PNG data URL
 */
function marbleTextureUrl({ hex, fleck }) {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const context = /** @type {CanvasRenderingContext2D} */ (canvas.getContext('2d'));

  context.fillStyle = hex;
  context.fillRect(0, 0, size, size);
  context.fillStyle = fleck;

  let seed = 7;
  const random = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };

  for (let i = 0; i < 170; i++) {
    const x = random() * size;
    const y = random() * size;
    const radius = 1.6 + random() * 3.4;

    // Draw wrapped copies so flecks continue across tile edges.
    for (const dx of [-size, 0, size]) {
      for (const dy of [-size, 0, size]) {
        context.beginPath();
        context.arc(x + dx, y + dy, radius, 0, Math.PI * 2);
        context.fill();
      }
    }
  }

  return canvas.toDataURL('image/png');
}

/**
 * Live 3D model shown over the main product photo, recoloured from the selected colour option.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewer - The model-viewer element.
 * @property {HTMLScriptElement} palette - JSON map of option value name to { hex, fleck? }.
 * @property {HTMLButtonElement} [toggle] - Switches between the model and the photo.
 *
 * @extends {Component<Refs>}
 */
class ColorModelViewer extends Component {
  requiredRefs = ['viewer', 'palette'];

  #controller = new AbortController();

  /** @type {Record<string, { hex: string, fleck?: string }>} */
  #palette = {};

  /** @type {Map<string, Promise<unknown>>} */
  #textures = new Map();

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#palette = JSON.parse(this.refs.palette.textContent || '{}');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.refs.viewer.removeAttribute('auto-rotate');
    }

    const scope = this.closest('.shopify-section') ?? document;
    scope.addEventListener('change', this.#handleChange, { signal });
    this.refs.viewer.addEventListener('load', this.#handleLoad, { signal });

    // Re-apply after the gallery moves this element into a re-rendered gallery.
    if (this.dataset.state === 'ready') this.#applyColor();

    // model-viewer defers the .glb download until the element is visible.
    requestIdleCallback(() => {
      loadModelViewer().catch((error) => {
        console.warn('[color-model-viewer]', error);
        this.dataset.state = 'error';
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Recolours the model to the given colour option value.
   * @param {string} value - The option value name, e.g. "Baby Pink".
   */
  setColor(value) {
    if (!(value in this.#palette)) return;

    this.dataset.color = value;
    this.toggleView(undefined, false);
    this.#applyColor();
  }

  /**
   * Switches between the 3D model and the product photo underneath.
   * @param {Event} [_event]
   * @param {boolean} [showPhoto] - Forces a view instead of toggling.
   */
  toggleView(_event, showPhoto = this.dataset.view !== 'photo') {
    this.dataset.view = showPhoto ? 'photo' : 'model';

    const { toggle } = this.refs;
    if (!toggle) return;

    toggle.setAttribute('aria-pressed', String(showPhoto));
    toggle.textContent = showPhoto ? 'Show 3D' : 'Show photo';
  }

  #handleLoad = () => {
    /** @type {any} */
    const viewer = this.refs.viewer;

    for (const material of viewer.model?.materials ?? []) {
      material.pbrMetallicRoughness.setMetallicFactor(1);
      material.pbrMetallicRoughness.setRoughnessFactor(1);
    }

    this.#disableToneMapping();
    this.#applyColor();
    this.dataset.state = 'ready';
  };

  /**
   * model-viewer 3.5 has no tone-mapping="none", so set it on its internal scene. Its version is
   * pinned in MODEL_VIEWER_URL; if the scene is not found, the neutral tone-mapping attribute and
   * exposure on the element remain as the fallback.
   */
  #disableToneMapping() {
    /** @type {any} */
    const viewer = this.refs.viewer;
    const scene = Object.getOwnPropertySymbols(viewer)
      .map((symbol) => viewer[symbol])
      .find((value) => value && typeof value === 'object' && 'toneMapping' in value && typeof value.queueRender === 'function');

    if (!scene) return;

    scene.toneMapping = NO_TONE_MAPPING;
    scene.queueRender();
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const { target } = event;

    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.closest('variant-picker') || target.closest('product-card, quick-add-dialog')) return;

    const optionName =
      target instanceof HTMLSelectElement ? target.selectedOptions[0]?.dataset.optionName : target.dataset.optionName;

    if (!optionName || optionName !== this.dataset.optionName) return;

    this.setColor(target.value);
  };

  async #applyColor() {
    const name = this.dataset.color ?? '';
    const color = this.#palette[name];
    /** @type {any} */
    const viewer = this.refs.viewer;
    const materials = viewer.model?.materials;

    if (!color || !materials) return;

    let texture = null;
    if (color.fleck) {
      if (!this.#textures.has(name)) {
        this.#textures.set(name, viewer.createTexture(marbleTextureUrl({ hex: color.hex, fleck: color.fleck })));
      }
      texture = await this.#textures.get(name);

      // A newer selection may have landed while the texture was being created.
      if (this.dataset.color !== name) return;
    }

    const rgb = (texture ? [1, 1, 1] : hexToLinearRgba(color.hex).slice(0, 3)).map((channel) => channel * LIGHT_GAIN);
    const lit = rgb.map((channel) => Math.min(1, channel * (1 - FLAT_SHARE)));
    const glow = rgb.map((channel) => Math.min(1, channel * FLAT_SHARE));

    for (const material of materials) {
      const pbr = material.pbrMetallicRoughness;
      pbr.baseColorTexture.setTexture(texture);
      material.emissiveTexture.setTexture(texture);
      pbr.setBaseColorFactor([...lit, 1]);
      material.setEmissiveFactor(glow);
    }
  }
}

if (!customElements.get('color-model-viewer')) {
  customElements.define('color-model-viewer', ColorModelViewer);
}
